import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import {
  Download,
  FileText,
  Package,
  Printer,
  ShoppingBag,
  Weight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  setBatchSummaryBatchIdTestId,
  setBatchSummaryTestId,
} from "./batch-summary.utils";

interface BatchSummaryProps {
  batchId: string;
  testId?: string;
}

export function BatchSummary({ batchId, testId }: BatchSummaryProps) {
  const { t } = useTranslation();
  // Mock batch data
  const batch = {
    id: batchId,
    status: "success" as const,
    totalOrders: 12,
    totalUnits: 36,
    totalWeight: 432, // oz
    carrier: "Beanz.com",
    createdAt: "2025-01-06 09:30 AM",
    printedAt: "2025-01-06 09:45 AM",
  };

  const statusConfig = {
    success: {
      label: t("views.batchSummary.status.completed"),
      className:
        "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400",
    },
    inProgress: {
      label: t("views.batchSummary.status.inProgress"),
      className:
        "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400",
    },
    error: {
      label: t("views.batchSummary.status.error"),
      className: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
    },
  };

  return (
    <Card
      testId={testId ?? setBatchSummaryTestId("container")}
      className="border-primary/20"
    >
      <CardContent className="p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h2
                  className="text-2xl font-bold font-mono"
                  data-testid={setBatchSummaryBatchIdTestId()}
                >
                  {batch.id}
                </h2>
                <Badge className={statusConfig[batch.status].className}>
                  {statusConfig[batch.status].label}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                {t("views.batchSummary.carrier")}: {batch.carrier} •{" "}
                {t("views.batchSummary.created")}: {batch.createdAt}
              </div>
            </div>
            <div className="flex gap-2">
              <Button>
                <FileText className="mr-2 h-4 w-4" />
                {t("views.batchSummary.generateManifest")}
              </Button>
              <Button variant="outline">
                <Printer className="mr-2 h-4 w-4" />
                {t("views.batchSummary.reprintLabels")}
              </Button>
              <Button variant="outline" size="icon">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="border-border">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="rounded-lg bg-primary/10 p-3">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{batch.totalOrders}</div>
                  <div className="text-sm text-muted-foreground">
                    {t("views.batchSummary.totalOrders")}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="rounded-lg bg-blue-500/10 p-3">
                  <ShoppingBag className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{batch.totalUnits}</div>
                  <div className="text-sm text-muted-foreground">
                    {t("views.batchSummary.totalUnits")}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="rounded-lg bg-purple-500/10 p-3">
                  <Weight className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{batch.totalWeight}</div>
                  <div className="text-sm text-muted-foreground">
                    {t("views.batchSummary.ozTotalWeight")}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="rounded-lg bg-green-500/10 p-3">
                  <Printer className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{batch.totalOrders}</div>
                  <div className="text-sm text-muted-foreground">
                    {t("views.batchSummary.labelsPrinted")}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
